import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Settings, Moon, Sun, Globe, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';

const SettingsPanel = ({ isOpen, onClose }) => {
  const { theme, setThemeMode } = useTheme();
  const { language, changeLanguage, availableLanguages, t } = useI18n();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-md bg-slate-900 border-white/10 dark:bg-slate-900 light:bg-white">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-heading flex items-center gap-2 text-foreground">
            <Settings className="w-5 h-5 text-amber-500" />
            {t('settings.title')}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="close-settings"
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Selection */}
          <div className="space-y-3">
            <Label className="text-slate-300 flex items-center gap-2">
              {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              {t('settings.theme')}
            </Label>
            <div className="flex gap-2">
              <Button
                variant={theme === 'light' ? 'default' : 'outline'}
                onClick={() => setThemeMode('light')}
                className={theme === 'light' 
                  ? 'bg-amber-500 text-black flex-1' 
                  : 'border-white/10 text-slate-300 flex-1'
                }
                data-testid="theme-light"
              >
                <Sun className="w-4 h-4 mr-2" />
                {t('settings.light')}
              </Button>
              <Button
                variant={theme === 'dark' ? 'default' : 'outline'}
                onClick={() => setThemeMode('dark')}
                className={theme === 'dark' 
                  ? 'bg-amber-500 text-black flex-1' 
                  : 'border-white/10 text-slate-300 flex-1'
                }
                data-testid="theme-dark"
              >
                <Moon className="w-4 h-4 mr-2" />
                {t('settings.dark')}
              </Button>
            </div>
          </div>

          {/* Language Selection */}
          <div className="space-y-3">
            <Label className="text-slate-300 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {t('settings.language')}
            </Label>
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-full bg-slate-800 border-white/10" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-white/10">
                {availableLanguages.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <div className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Map Lines Toggle Info */}
          <div className="p-3 rounded-lg bg-slate-800/50 border border-white/10">
            <p className="text-xs text-slate-400">
              💡 Tip: Toggle sun/moon lines on the map using the layers button in the planner view.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPanel;
